import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { BiChevronLeft } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';

import { useGetTransactionsByPackageIdQuery } from '@/app/services/package-rtk.service';
import ApiFailure from '@/components/ui/ApiFailure';
import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import Pagination from './components/Pagination';
import TransactionCard from './components/TransactionCard';

interface IState {
  package_id: string;
  size: string;
  page: string;
}

// Define the action types
type Action = { type: 'SET_SIZE'; payload: string } | { type: 'SET_PAGE'; payload: string };

// Define the initial state
const initialState: IState = {
  package_id: '',
  page: '1',
  size: '10',
};

// Define the reducer
const reducer = (state: IState, action: Action): IState => {
  switch (action.type) {
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SET_SIZE':
      return { ...state, size: action.payload };
    default:
      return state;
  }
};

function PackageTransactions() {
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const package_id = queryParams.get('package_id');
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);

  const { data, isLoading, isSuccess, isError, refetch } = useGetTransactionsByPackageIdQuery(
    {
      package_id: package_id as string,
      page: state.page,
      limit: state.size,
    },
    { skip: !package_id }
  );

  let content;

  if (isLoading)
    content = (
      <div className="tw-flex tw-justify-center tw-pt-10">
        <div className="tw-w-[250px] tw-animate-pulse tw-bg-gray-100 tw-rounded-xl tw-p-6">Loading ...</div>
      </div>
    );

  if (isError)
    content = (
      <div className="tw-self-stretch tw-px-6 tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-center tw-items-center tw-gap-4 tw-inline-flex">
        <ApiFailure refetch={refetch} />
      </div>
    );

  if (isSuccess && data) {
    content = (
      <div className="tw-w-full tw-p-0 tw-flex tw-flex-col tw-gap-6 tw-relative">
        {data.transactions.length === 0 ? (
          <div className="tw-text-center tw-text-gray-500 tw-py-10">{t('marketplace/transactions/noTransactions')}</div>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-6">
            <div className="tw-flex tw-flex-col tw-gap-6 tw-w-full">
              {data.transactions.map((tx: any) => (
                <TransactionCard key={tx.transaction_id} tx={tx} />
              ))}
            </div>
            <div className="">
              <Pagination pagination={data.pagination} onPageChange={page => dispatch({ type: 'SET_PAGE', payload: '' + page })} />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tw-p-4 tw-bg-gray-50 tw-min-h-screen tw-relative tw-flex tw-flex-col tw-w-full">
      <div className="tw-flex tw-flex-col tw-items-start tw-justify-center tw-w-full">
        <PSButton
          id="btn-transaction-back"
          variant="none"
          type="button"
          icon={<BiChevronLeft size={24} />}
          onClick={() => navigate(-1)}
          className="tw-flex tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-text-gray-700 hover:tw-text-gray-900 tw-pl-0"
          title={t('Wizard/general/key6')}
        />
        <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-6">{t('marketplace/transactions/title')}</h1>
      </div>
      {content}
    </div>
  );
}

export default WrappedErrorBoundary(PackageTransactions);
